// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadBargainBinProducts from "@awc/App/Handlers/BargainBin/GetBargainProducts";
import { CustomAppState } from "@awc/App/Store/Reducers";
import DataTable from "@awc/MobiusOverrides/DataTable/DataTable";
import ProductAddToCartButton from "@awc/Overrides/Components/ProductAddToCartButton";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProduct, ProductContext, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState, ownProps: any) => {
    return {
        bargain: state.xBargain.bargains,
        variantTraits: getProductState(state, ownProps.parentProductId).value?.variantTraits,
        variantChildren: getVariantChildrenDataView(state, ownProps.parentProductId).value,
        isLoadingBargains: state.xBargain.isLoadingBargains,
    };
};
const mapDispatchToProps = {
    loadBargainBinProducts,
};

type Props = WidgetProps &
    HasProduct &
    ReturnType<typeof mapStateToProps> &
    HasParentProductId &
    ResolveThunks<typeof mapDispatchToProps> & { isBargainBinPage: boolean };

export interface ProductDetailsBargainStyles {
    container?: InjectableCss;
    mainTitle?: TypographyProps;
    tableHeadText?: TypographyProps;
    dataCells?: DataTableCellProps;
    dataCellsHead?: DataTableCellProps;
    dataTable?: DataTableProps;
    dataHeader?: DataTableHeaderProps;
}

export const productDetailsBargainStyles: ProductDetailsBargainStyles = {};

const styles = productDetailsBargainStyles;

const ProductDetailsBargain: React.FC<Props> = ({
    product,
    loadBargainBinProducts,
    bargain,
    isBargainBinPage,
    variantChildren,
    isLoadingBargains,
}) => {
    useEffect(() => {
        if (isBargainBinPage) {
            return;
        }
        !isLoadingBargains &&
            loadBargainBinProducts({
                productId: product.id,
            });
    }, [product?.id]);

    // sort on length
    const sortedDiscountedProducts = useMemo(() => {
        const products = bargain?.discountedProducts ?? [];
        return [...products].sort((p1, p2) => p1.length - p2.length);
    }, [bargain?.discountedProducts]);

    // Create (id → ProductModel) from variantChildren
    const mappedProducts = useMemo(
        () =>
            (variantChildren?.reduce((acc, product) => ({ ...acc, [product.id]: product }), {}) || {}) as {
                [id: string]: ProductModel;
            },
        [variantChildren],
    );

    // Collect all "color" trait value IDs from the product
    const traits = useMemo(() => {
        return (
            product.variantTraits?.reduce((acc, variant) => {
                if (variant.nameDisplay.toLowerCase() === "color") {
                    variant.traitValues?.forEach(traitValue => {
                        acc.push(traitValue.id);
                    });
                }
                return acc;
            }, [] as string[]) || []
        );
    }, [product.variantTraits]);

    if ((!bargain || (bargain.discountedProducts && bargain.discountedProducts?.length <= 0)) && !isBargainBinPage) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.container} key={product?.id}>
                <Typography {...styles.mainTitle}>
                    {translate("Bargain Bin: Reel in the savings and buy {0} at deep discounts.", product.productTitle)}
                </Typography>
                <Typography {...styles.tableHeadText}>{siteMessage("BargainBin_TableHeader")}</Typography>
                <DataTable {...styles.dataTable} data-test-selector="BargainBin_TableHeader">
                    <DataTableBody>
                        <DataTableRow>
                            <DataTableCell {...styles.dataCellsHead}>{translate("Length")}</DataTableCell>
                            <DataTableCell {...styles.dataCellsHead}>{translate("Price")}</DataTableCell>
                            <DataTableCell {...styles.dataCellsHead}>{translate("Color")}</DataTableCell>
                            <DataTableCell {...styles.dataCellsHead}>{translate("Description")}</DataTableCell>
                            <DataTableCell {...styles.dataCellsHead}></DataTableCell>
                        </DataTableRow>

                        {!!sortedDiscountedProducts &&
                            sortedDiscountedProducts.map((prod: any) => {
                                let xProduct: ProductModel | null = null;
                                if (prod.product?.id) {
                                    xProduct = mappedProducts[prod.product.id as string];
                                }

                                // Find the first child trait value whose id matches one of the collected color trait ids
                                const childrenTraits = xProduct?.childTraitValues?.find(child =>
                                    traits?.includes?.(child.id),
                                );

                                return (
                                    <DataTableRow key={prod.id}>
                                        <DataTableCell {...styles.dataCells}>{prod.length}</DataTableCell>
                                        <DataTableCell {...styles.dataCells}>${prod.price}</DataTableCell>
                                        <DataTableCell {...styles.dataCells}>
                                            {childrenTraits?.valueDisplay}
                                        </DataTableCell>
                                        <DataTableCell {...styles.dataCells}>{prod.colorDescription}</DataTableCell>
                                        <DataTableCell {...styles.dataCells}>
                                            <ProductContext.Provider
                                                value={{
                                                    product: prod.product,
                                                    productInfo: {
                                                        pricing: prod.product.pricing,
                                                        unitOfMeasure: prod.product.unitOfMeasure,
                                                        qtyOrdered: 1,
                                                    } as any,
                                                }}
                                            >
                                                <ProductAddToCartButton
                                                    extendedStyles={{
                                                        variant: "tertiary",
                                                        sizeVariant: "small",
                                                    }}
                                                    bargainProps={{
                                                        properties: {
                                                            BargainBinLength: prod.length,
                                                            LRB: prod.lrb,
                                                        },
                                                    }}
                                                />
                                            </ProductContext.Provider>
                                        </DataTableCell>
                                    </DataTableRow>
                                );
                            })}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsBargain))),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
